import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authStore } from '@/state/auth';
import { api } from '@marine/api-client/api';
import { disposeCentrifuge } from '@/lib/realtime';
import { toast } from 'sonner';
import type { User } from '@/types/auth';
import { usePreferencesStore } from '@/state/preferences';
import type { PreferencesTree } from '@/types/preferences';

interface LoginCredentials {
  email: string;
  password: string;
  remember?: boolean;
}

/**
 * Get current auth state
 */
export const useAuth = () => authStore();

/**
 * Get current user
 */
export const useUser = () => authStore((state) => state.user);

/**
 * Login hook with Laravel Sanctum SPA authentication
 */
export function useLogin() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      // CSRF is seeded by the same-origin blade shell (W4 D5). No pre-call; the
      // shared 419 interceptor recovers on rotation/expiry.
      const { data } = await api.post('/login', credentials);
      return data;
    },
    onSuccess: (data) => {
      // Update auth state
      authStore.getState().login(data.user);
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    // TODO: Replace any with a typed error shape once API response types are defined.
    onError: (error: any) => {
      console.error('Login error:', error.response?.data || error.message);
    },
  });
}

/**
 * Logout hook
 */
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      authStore.getState().logout();
      queryClient.clear();
      disposeCentrifuge();
      toast.info('Logged out successfully');
    },
    onError: () => {
      // Even if API call fails, clear local state
      authStore.getState().logout();
      queryClient.clear();
      disposeCentrifuge();
    },
  });
};

/**
 * Get current authenticated user from API
 */
export const useCurrentUser = () => {
  const isAuthenticated = authStore((state) => state.isAuthenticated);

  const query = useQuery<User>({
    queryKey: ['me'],
    queryFn: () => api.getOne<User>('/api/me'),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Sync user + preferences whenever /me returns fresh data. React Query only
  // produces a new `query.data` reference when the fetched payload actually
  // changes, so this effect fires on real updates — not on every render.
  useEffect(() => {
    if (query.data) {
      const currentUser = authStore.getState().user;
      if (currentUser?.id !== query.data.id || currentUser?.email !== query.data.email) {
        authStore.getState().setUser(query.data);
      }
      // Hydrate the preferences store with the server's truth whenever /me resolves.
      usePreferencesStore.getState().setAll(
        (query.data.preferences ?? {}) as PreferencesTree
      );
    }
  }, [query.data]);

  return query;
};
